var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import i18next from 'i18next';
import { chain, constant, groupBy, mapValues, sortBy, times } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { PlanType, PlanTypeCategory, StatusPermission, lowestPermission } from '../../enum';
import { useAPI, useAuthenticatedAPI } from '../../state';
import { toInt } from '../../util';
import { useUserPermissionsAPI } from '../app/selectors';
import { useMultiskillLocations } from '../multiskill/multiskillGroup/multiskillLocationsContext';
import { ALL_LOCATIONS_ID, STEP_NAME, WizardStep, getStepsLength, getWorkflowSteps } from './constants';
import { useQueueAPI } from './queue/selectors';
import { WageType } from './supportStaff/types';
import { useWizardSimpleContext } from './useWizardSimpleContext';
function translate(key) {
  return i18next.t(key, {
    defaultValue: key
  });
}
var PLAN_API = '/api/plan/{basePlanId}/{id}';
var PLAN_FX_RATES_API = '/api/plan/{basePlanId}/fx-rates/{id}';
var PLAN_FX_RATES_SAVE_API = '/api/plan/{basePlanId}/fx-rates';
var SCENARIO_MULTISKILL_API = '/api/multiskill/scenario/{basePlanId}';
var EMPTY_QUEUE_LOCATIONS = [];
export var SUPPORTED_LOCALES = ['en', 'fr'];
export var MONTH_OF_YEAR = 12;
export var TimelineType;
(function (TimelineType) {
  TimelineType[TimelineType["Years"] = 0] = "Years";
  TimelineType[TimelineType["Weeks"] = 1] = "Weeks";
  TimelineType[TimelineType["Dates"] = 2] = "Dates";
})(TimelineType || (TimelineType = {}));
export var selectPlan = function (plan) {
  return plan;
};
export var selectPlanType = createSelector(selectPlan, function (plan) {
  var _a;
  return (_a = plan === null || plan === void 0 ? void 0 : plan.planType) !== null && _a !== void 0 ? _a : PlanType.Monthly;
});
export var selectPlanTypeCategory = createSelector(selectPlan, function (plan) {
  var category = plan === null || plan === void 0 ? void 0 : plan.planTypeCategory;
  if (category === PlanTypeCategory.ProdHours) {
    return PlanTypeCategory.ProdHours;
  }
  return PlanTypeCategory.WorkVolume;
});
export var selectChunkSize = createSelector(selectPlan, function (plan) {
  return !plan || plan.planType === PlanType.Monthly ? MONTH_OF_YEAR : plan.periodLength;
});
var selectYears = createSelector(selectPlan, selectChunkSize, function (plan, chunkSize) {
  if (!plan || plan.periodLength === chunkSize) return undefined;
  return times(plan.periodLength / chunkSize, function (n) {
    return "".concat(translate('table.year'), " ").concat(n + 1);
  });
});
export var selectWeeks = createSelector(selectPlan, function (plan) {
  if (!plan || (plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.Monthly) return undefined;
  return times(plan.periodLength, function (n) {
    return "".concat((plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.Weekly ? translate('table.week') : translate('table.month'), " ").concat(n + 1);
  });
});
export var selectWeeksWithLocale = function (t) {
  return createSelector(selectPlan, function (plan) {
    if (!plan || (plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.Monthly) return undefined;
    var label = (plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.Weekly ? t('week') : t('month');
    return times(plan.periodLength, function (n) {
      return "".concat(label, " ").concat(n + 1);
    });
  });
};
export var selectPeriods = createSelector(selectPlan, function (plan) {
  return plan && times(plan.periodLength, function (n) {
    return moment.utc(plan.startDate).add(n, plan.planType === PlanType.Weekly ? 'w' : 'M');
  });
});
export var selectMonthly = createSelector(selectPeriods, selectPlan, function (periods, plan) {
  return (plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.Monthly ? periods === null || periods === void 0 ? void 0 : periods.map(function (m) {
    return m.format(translate('dateFormat.monthYearFormat'));
  }) : undefined;
});
export var selectMonthlyCustom = createSelector(selectPeriods, selectPlan, function (periods, plan) {
  return (plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.MonthlyCustom ? periods === null || periods === void 0 ? void 0 : periods.map(function (m) {
    return m.format(translate('dateFormat.monthYearFormat'));
  }) : undefined;
});
export var selectWeekDate = createSelector(selectPeriods, selectPlan, function (periods, plan) {
  return (plan === null || plan === void 0 ? void 0 : plan.planType) === PlanType.Weekly ? periods === null || periods === void 0 ? void 0 : periods.map(function (m) {
    return m.format(translate('dateFormat.dayMonthYearFormat'));
  }) : undefined;
});
export var createPeriodSelector = function (t, isBold) {
  if (isBold === void 0) {
    isBold = false;
  }
  return createSelector(selectPeriods, selectPlan, function (periods, plan) {
    if (!plan || plan.planType === PlanType.Monthly) return undefined;
    var isWeekly = plan.planType === PlanType.Weekly;
    var format = isWeekly ? t('dateFormat.dayMonthYearFormat') : t('dateFormat.monthYearFormat');
    var label = isWeekly ? t('chart.week') : t('chart.month');
    return periods === null || periods === void 0 ? void 0 : periods.map(function (period, index) {
      var periodNumber = index + 1;
      var formattedPeriod = period.format(format);
      var displayPeriod = isBold ? formattedPeriod.toUpperCase() : formattedPeriod;
      if (isBold) {
        return "<b>".concat(label, " ").concat(periodNumber, "</b><br>").concat(displayPeriod);
      }
      return "".concat(label, " ").concat(periodNumber, " (").concat(displayPeriod, ")");
    });
  });
};
export var selectFullWeeklyBr = function (t) {
  return createPeriodSelector(t, true);
};
export var selectFullWeekly = function (t) {
  return createPeriodSelector(t, false);
};
var selectBusinessDays = createSelector(selectPeriods, selectPlanType, function (periods, planType) {
  return periods === null || periods === void 0 ? void 0 : periods.map(function (m) {
    return planType === PlanType.Weekly ? [5, 7] : [m.endOf('month').businessDaysIntoMonth(), moment(m).daysInMonth()];
  });
});
export var createTimelineSelector = function (includeYear, includeWeek, includeWeekDate, addColumns) {
  return createSelector(selectYears, selectWeeks, selectMonthly, selectMonthlyCustom, selectWeekDate, selectChunkSize, function (years, weeks, monthly, monthlyCustom, weekDate, chunkSize) {
    var _a;
    return [includeYear ? years : undefined, includeWeek && weeks ? __spreadArray(__spreadArray([], weeks, true), addColumns, true) : undefined, (_a = monthly && chain(monthly).chunk(chunkSize).map(function (v) {
      return __spreadArray(__spreadArray([], v, true), addColumns, true);
    }).flatten().value()) !== null && _a !== void 0 ? _a : weekDate && includeWeekDate && __spreadArray(__spreadArray([], weekDate, true), times(addColumns.length, constant('')), true), includeWeekDate && monthlyCustom ? __spreadArray(__spreadArray([], monthlyCustom, true), times(addColumns.length, constant('')), true) : undefined].map(function (v, i) {
      return v && [v, i];
    }).filter(function (v) {
      return !!v;
    });
  });
};
export var createWizardDetailTimelineSelector = function (addColumns) {
  return createSelector(selectMonthly, selectMonthlyCustom, selectWeekDate, selectChunkSize, function (monthly, monthlyCustom, weekDate, chunkSize) {
    var _a;
    var dates = (_a = monthly !== null && monthly !== void 0 ? monthly : monthlyCustom) !== null && _a !== void 0 ? _a : weekDate;
    if (!dates) return [];
    var titles = monthly ? chain(dates).chunk(chunkSize).map(function (periods) {
      return __spreadArray(__spreadArray([], periods, true), addColumns, true);
    }).flatten().value() : __spreadArray(__spreadArray([], dates, true), addColumns, true);
    return [[titles, TimelineType.Dates]];
  });
};
export var createWizardPeriodLabelTimelineSelector = function (addColumns) {
  return createSelector(selectPlan, selectChunkSize, function (plan, chunkSize) {
    if (!plan) return [];
    var label = plan.planType === PlanType.Weekly ? translate('table.week') : translate('table.month');
    var periodLabels = times(plan.periodLength, function (n) {
      return "".concat(label, " ").concat(n + 1);
    });
    var titles = plan.planType === PlanType.Monthly ? chain(periodLabels).chunk(chunkSize).map(function (periods) {
      return __spreadArray(__spreadArray([], periods, true), addColumns, true);
    }).flatten().value() : __spreadArray(__spreadArray([], periodLabels, true), addColumns, true);
    return [[titles, TimelineType.Weeks]];
  });
};
var selectMonthlyOpts = createSelector(selectPeriods, selectPlan, function (periods, plan) {
  if (!plan || plan.planType === PlanType.Weekly) return undefined;
  var monthFormat = plan.planType === PlanType.Monthly ? translate('dateFormat.longMonthYearFormat') : translate('dateFormat.monthYearFormat');
  return periods === null || periods === void 0 ? void 0 : periods.map(function (m) {
    return m.format(monthFormat);
  });
});
export var selectTimelineOptions = createSelector(selectMonthlyOpts, selectWeekDate, selectWeeks, function (monthly, weekly, weeks) {
  var _a;
  return ((_a = monthly !== null && monthly !== void 0 ? monthly : weekly) !== null && _a !== void 0 ? _a : []).map(function (name, id) {
    return {
      id: id,
      name: weeks ? "".concat(weeks[id], " (").concat(name, ")") : name
    };
  });
});
export var usePlanAPI = function () {
  var _a = useParams(),
    basePlanId = _a.basePlanId,
    id = _a.id;
  return useAPI(PLAN_API, {
    basePlanId: basePlanId,
    id: id
  });
};
export var usePlanFxRatesAPI = function () {
  var _a = useParams(),
    basePlanId = _a.basePlanId,
    id = _a.id;
  return useAuthenticatedAPI(PLAN_FX_RATES_API, {
    basePlanId: basePlanId,
    id: id
  }, 'GET');
};
export var useSavePlanFxRatesAPI = function () {
  var basePlanId = useParams().basePlanId;
  return useAuthenticatedAPI(PLAN_FX_RATES_SAVE_API, {
    basePlanId: basePlanId
  }, 'PUT');
};
export var usePlanType = function () {
  var _a;
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  return selectPlanType(plan);
};
export var usePlanTypeCategory = function () {
  var _a;
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  return selectPlanTypeCategory(plan);
};
export var useDetailTimeline = function () {
  var _a;
  var addColumns = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    addColumns[_i] = arguments[_i];
  }
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  var selectTimelines = useMemo(function () {
    return createWizardDetailTimelineSelector(addColumns);
  }, [addColumns]);
  return selectTimelines(plan);
};
export var usePeriodLabelTimeline = function () {
  var _a;
  var addColumns = [];
  for (var _i = 0; _i < arguments.length; _i++) {
    addColumns[_i] = arguments[_i];
  }
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  var selectTimelines = useMemo(function () {
    return createWizardPeriodLabelTimelineSelector(addColumns);
  }, [addColumns]);
  return selectTimelines(plan);
};
export var useBusinessDays = function () {
  var data = usePlanAPI()[0].data;
  return selectBusinessDays(data);
};
export var usePeriods = function () {
  var _a;
  var data = usePlanAPI()[0].data;
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  var activePlan = data !== null && data !== void 0 ? data : plan;
  return useMemo(function () {
    return selectPeriods(activePlan);
  }, [activePlan]);
};
export var useWizardParams = function () {
  var _a;
  var _b = useParams(),
    basePlanId = _b.basePlanId,
    id = _b.id;
  var _c = useSearchParams(),
    searchParams = _c[0],
    setSearchParams = _c[1];
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  var planTypeCategory = selectPlanTypeCategory(plan);
  var result = useMemo(function () {
    var workflowSteps = getWorkflowSteps(planTypeCategory);
    var stepsLength = getStepsLength(planTypeCategory);
    // Get the step parameter from URL, default to 0 if not provided or invalid
    var urlStep = searchParams.get('step');
    var parsedStep = urlStep ? Number.parseInt(urlStep, 10) : 0;
    // Clamp the step index to valid range
    var stepIndex = Math.max(0, Math.min(Number.isNaN(parsedStep) ? 0 : parsedStep, stepsLength - 1));
    var step = workflowSteps[stepIndex];
    // If step is undefined, fall back to the first step
    var finalStep = step !== null && step !== void 0 ? step : workflowSteps[0];
    return {
      basePlanId: toInt(basePlanId),
      id: toInt(id),
      queueId: toInt(searchParams.get('queueId')),
      step: finalStep,
      stepName: STEP_NAME[finalStep],
      stepIndex: stepIndex,
      needsRedirect: urlStep && (Number.isNaN(parsedStep) || parsedStep < 0 || parsedStep >= stepsLength)
    };
  }, [basePlanId, id, searchParams, planTypeCategory]);
  // Redirect if URL has invalid step parameter
  useEffect(function () {
    if (result.needsRedirect) {
      var newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('step', result.stepIndex.toString());
      setSearchParams(newSearchParams, {
        replace: true
      });
    }
  }, [result.needsRedirect, result.stepIndex, searchParams, setSearchParams]);
  return {
    basePlanId: result.basePlanId,
    id: result.id,
    queueId: result.queueId,
    step: result.step,
    stepName: result.stepName,
    stepIndex: result.stepIndex
  };
};
export var usePeriodChunkSize = function () {
  var _a;
  var plan = ((_a = useWizardSimpleContext()) !== null && _a !== void 0 ? _a : {}).plan;
  return selectChunkSize(plan);
};
export var useDeleteAllMultiskillAPI = function () {
  var basePlanId = useParams().basePlanId;
  return useAuthenticatedAPI(SCENARIO_MULTISKILL_API, {
    basePlanId: basePlanId
  }, 'DELETE');
};
export var useQueueLocations = function () {
  var _a, _b;
  var queueData = useQueueAPI()[0];
  return (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) !== null && _b !== void 0 ? _b : EMPTY_QUEUE_LOCATIONS;
};
export var useGroupedLocations = function () {
  var locations = useQueueLocations();
  return useMemo(function () {
    var sortedLocations = sortBy(locations, [function (l) {
      var _a, _b;
      return (_b = (_a = l.planLocation) === null || _a === void 0 ? void 0 : _a.locationType) !== null && _b !== void 0 ? _b : '';
    }, function (l) {
      var _a, _b;
      return (_b = (_a = l.planLocation) === null || _a === void 0 ? void 0 : _a.country) !== null && _b !== void 0 ? _b : '';
    }, function (l) {
      var _a, _b;
      return (_b = (_a = l.planLocation) === null || _a === void 0 ? void 0 : _a.locationName) !== null && _b !== void 0 ? _b : '';
    }]);
    return mapValues(groupBy(sortedLocations, function (l) {
      var _a, _b;
      return (_b = (_a = l.planLocation) === null || _a === void 0 ? void 0 : _a.locationType) !== null && _b !== void 0 ? _b : '';
    }), function (byType) {
      return groupBy(byType, function (l) {
        var _a, _b;
        return (_b = (_a = l.planLocation) === null || _a === void 0 ? void 0 : _a.country) !== null && _b !== void 0 ? _b : '';
      });
    });
  }, [locations]);
};
export var useWageTypeOptions = function () {
  var t = useTranslation('wizard', {
    keyPrefix: 'financial'
  }).t;
  return useMemo(function () {
    return [{
      id: WageType.AnnualSalary,
      name: t('wageTypeOptions.annualSalary')
    }, {
      id: WageType.Hourly,
      name: t('wageTypeOptions.hourly')
    }];
  }, [t]);
};
export var useLocationIndexMap = function () {
  var queueLocations = useQueueLocations();
  return useMemo(function () {
    return new Map(queueLocations.map(function (l, i) {
      return [l.id, i];
    }));
  }, [queueLocations]);
};
// this hook use to check locked plan
export var useIsPlanLocked = function () {
  var _a;
  var step = useWizardParams().step;
  var planData = usePlanAPI()[0];
  return !!((_a = planData.data) === null || _a === void 0 ? void 0 : _a.isLocked) && (planData.data.isBaseline || step === WizardStep.Queues);
};
/**
 * Downgrades an org-wide permission (capacity, financial, actual, recruitment) by the open
 * plan's client/LOB permission (lowest wins). A view-only plan is read-only in every regard,
 * regardless of the caller's org-wide permission for that feature.
 */
export var useEffectivePlanPermission = function (orgPermission) {
  var _a, _b;
  var planData = usePlanAPI()[0];
  return lowestPermission(orgPermission !== null && orgPermission !== void 0 ? orgPermission : StatusPermission.Edit, (_b = (_a = planData.data) === null || _a === void 0 ? void 0 : _a.effectivePermission) !== null && _b !== void 0 ? _b : StatusPermission.Edit);
};
/**
 * Capacity permission for the open plan: the user's org-wide capacity permission downgraded by
 * the plan's client/LOB permission (lowest wins). Use this instead of
 * `useUserPermissionsAPI().capacityPermission` wherever a specific plan is open.
 */
export var useEffectiveCapacityPermission = function () {
  var capacityPermission = useUserPermissionsAPI().capacityPermission;
  return useEffectivePlanPermission(capacityPermission);
};
export var useMultiLangTranslations = function (i18n, i18nKeys, ns) {
  return useMemo(function () {
    if (!i18n) return new Set();
    var names = new Set();
    for (var _i = 0, i18nKeys_1 = i18nKeys; _i < i18nKeys_1.length; _i++) {
      var key = i18nKeys_1[_i];
      for (var _a = 0, SUPPORTED_LOCALES_1 = SUPPORTED_LOCALES; _a < SUPPORTED_LOCALES_1.length; _a++) {
        var locale = SUPPORTED_LOCALES_1[_a];
        var value = i18n.t(key, {
          lng: locale,
          ns: ns
        });
        if (value !== key) {
          names.add(value);
        }
      }
    }
    return names;
  }, [i18n, i18nKeys, ns]);
};
export var useEffectiveQueueLocations = function () {
  var _a;
  var multiskillCtx = useMultiskillLocations();
  var wizardLocations = useQueueLocations();
  return (_a = multiskillCtx === null || multiskillCtx === void 0 ? void 0 : multiskillCtx.locations) !== null && _a !== void 0 ? _a : wizardLocations;
};
export var useEffectiveGroupedLocations = function () {
  var _a;
  var multiskillCtx = useMultiskillLocations();
  var wizardGroupedLocations = useGroupedLocations();
  return (_a = multiskillCtx === null || multiskillCtx === void 0 ? void 0 : multiskillCtx.groupedLocations) !== null && _a !== void 0 ? _a : wizardGroupedLocations;
};
export var useEffectiveLocationIndexMap = function () {
  var _a;
  var multiskillCtx = useMultiskillLocations();
  var wizardLocationIndexMap = useLocationIndexMap();
  return (_a = multiskillCtx === null || multiskillCtx === void 0 ? void 0 : multiskillCtx.locIndexMap) !== null && _a !== void 0 ? _a : wizardLocationIndexMap;
};
export var useLocationOptions = function () {
  var t = useTranslation('translation').t;
  var queueLocations = useEffectiveQueueLocations();
  return useMemo(function () {
    var all = {
      id: ALL_LOCATIONS_ID,
      name: t('quickInput.allLocations')
    };
    var source = queueLocations.map(function (l) {
      var _a;
      return {
        id: l.id,
        name: (_a = l.planLocation) === null || _a === void 0 ? void 0 : _a.locationName
      };
    });
    return __spreadArray([all], source, true);
  }, [queueLocations, t]);
};